import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { AlertTriangle, Clock, Mail, UserX } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Warnings = () => {
  const [expiringUsers, setExpiringUsers] = useState([]);
  const [expiredUsers, setExpiredUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expiringRes, expiredRes] = await Promise.all([
        axios.get(`${API}/stats/expiring`),
        axios.get(`${API}/stats/expired`)
      ]);
      setExpiringUsers(expiringRes.data);
      setExpiredUsers(expiredRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleLockUser = async (userId, isLocked) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_locked: !isLocked });
      toast.success(isLocked ? "Teilnehmer entsperrt" : "Teilnehmer gesperrt");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Ändern");
    }
  };

  return (
    <AdminLayout title="Warnungen & Ablaufende Abos">
      <div className="space-y-8" data-testid="warnings-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card border-border border-l-4 border-l-yellow-500">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Bald ablaufend (≤5 Tage)</p>
                  <p className="text-3xl font-bold text-yellow-500">{expiringUsers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border border-l-4 border-l-red-500">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                  <UserX className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Abgelaufen</p>
                  <p className="text-3xl font-bold text-red-500">{expiredUsers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Expiring Soon */}
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="flex items-center gap-2 text-yellow-500">
                  <AlertTriangle className="h-5 w-5" />
                  Bald ablaufende Abonnements (≤5 Tage)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {expiringUsers.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine bald ablaufenden Abos</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground">Teilnehmer</TableHead>
                        <TableHead className="text-muted-foreground">E-Mail</TableHead>
                        <TableHead className="text-muted-foreground">Abo-Typ</TableHead>
                        <TableHead className="text-muted-foreground">Ende</TableHead>
                        <TableHead className="text-muted-foreground">Restlaufzeit</TableHead>
                        <TableHead className="text-muted-foreground text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expiringUsers.map((user) => (
                        <TableRow key={user.id} className="border-border" data-testid={`expiring-${user.id}`}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell className="text-muted-foreground font-mono text-sm">{user.email}</TableCell>
                          <TableCell>
                            <Badge className="status-warning rounded-sm">
                              {user.subscription?.subscription_type === "monthly" ? "Monatlich" : user.subscription?.subscription_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{user.subscription?.end_date}</TableCell>
                          <TableCell>
                            <span className="font-bold text-yellow-500">
                              {user.subscription?.days_remaining} Tage
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" className="gap-1">
                              <Mail className="h-4 w-4" />
                              Benachrichtigen
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Expired */}
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="flex items-center gap-2 text-red-500">
                  <UserX className="h-5 w-5" />
                  Abgelaufene Abonnements
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {expiredUsers.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <UserX className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine abgelaufenen Abos</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground">Teilnehmer</TableHead>
                        <TableHead className="text-muted-foreground">E-Mail</TableHead>
                        <TableHead className="text-muted-foreground">Abo-Typ</TableHead>
                        <TableHead className="text-muted-foreground">Abgelaufen am</TableHead>
                        <TableHead className="text-muted-foreground">Status</TableHead>
                        <TableHead className="text-muted-foreground text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expiredUsers.map((user) => (
                        <TableRow key={user.id} className="border-border" data-testid={`expired-${user.id}`}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell className="text-muted-foreground font-mono text-sm">{user.email}</TableCell>
                          <TableCell>
                            <Badge className="status-expired rounded-sm">
                              {user.subscription?.subscription_type === "monthly" ? "Monatlich" : user.subscription?.subscription_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm text-red-500">{user.subscription?.end_date}</TableCell>
                          <TableCell>
                            {user.is_locked ? (
                              <Badge className="status-expired rounded-sm">Gesperrt</Badge>
                            ) : (
                              <Badge variant="outline" className="rounded-sm">Offen</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant={user.is_locked ? "default" : "destructive"}
                              size="sm"
                              onClick={() => handleLockUser(user.id, user.is_locked)}
                              data-testid={`lock-${user.id}`}
                            >
                              {user.is_locked ? "Entsperren" : "Sperren"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Warnings;
